import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { publicApi } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { GameController, ArrowRight, ArrowsClockwise, Heart, Globe, Code, Palette, Cpu } from "@phosphor-icons/react";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Showcase() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang, setLang } = useI18n();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [remixing, setRemixing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const p = await publicApi.get(slug);
        setProject(p);
      } catch {
        setProject(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const handleRemix = async () => {
    if (!user) {
      navigate("/");
      return;
    }
    setRemixing(true);
    try {
      const p = await publicApi.remix(slug);
      navigate(`/project/${p.project_id}`);
    } catch {
      toast.error(t("toast.saveFailed"));
    } finally {
      setRemixing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950 text-zinc-500 font-mono text-xs tracking-[0.3em] uppercase gf-blink">
        LOADING SHOWCASE...
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-zinc-950 text-zinc-300 gap-4 p-6">
        <div className="text-[10px] tracking-[0.4em] uppercase text-cyan-400">404</div>
        <h1 className="font-display text-3xl tracking-tighter font-light">Public game not found</h1>
        <p className="text-sm text-zinc-500">This project may be private or no longer exists.</p>
        <Link to="/" className="mt-4">
          <Button className="bg-cyan-400 hover:bg-cyan-300 text-zinc-950 rounded-none font-semibold tracking-wide">Go home</Button>
        </Link>
      </div>
    );
  }

  const counts = {
    characters: project.entities?.characters?.length || 0,
    enemies: project.entities?.enemies?.length || 0,
    bosses: project.entities?.bosses?.length || 0,
    items: project.entities?.items?.length || 0,
    weapons: project.entities?.weapons?.length || 0,
    vehicles: project.entities?.vehicles?.length || 0,
  };

  const totalEntities = Object.values(counts).reduce((a, b) => a + b, 0);
  const sprites = (project.sprites || []).slice(0, 8);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 gf-grain">
      <header className="gf-glass border-b border-zinc-800 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="showcase-brand">
            <div className="w-7 h-7 bg-cyan-400 flex items-center justify-center">
              <GameController size={18} weight="duotone" className="text-zinc-950" />
            </div>
            <span className="font-display text-xl tracking-tight">GameForge</span>
            <span className="hidden md:inline text-[10px] tracking-[0.3em] text-zinc-500 uppercase ml-3 pl-3 border-l border-zinc-800">
              SHOWCASE
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setLang(lang === "es" ? "en" : "es")}
              className="text-[11px] tracking-[0.25em] uppercase text-zinc-400 hover:text-cyan-400 px-3 py-2 border border-zinc-800 hover:border-cyan-500/50 transition-colors flex items-center gap-2"
              data-testid="showcase-lang-toggle"
            >
              <Globe size={14} /> {lang.toUpperCase()}
            </button>
            <Link to="/dashboard">
              <Button className="bg-cyan-400 hover:bg-cyan-300 text-zinc-950 rounded-none font-semibold tracking-wide text-xs gap-2 px-4 py-2 h-auto">
                {t("showcase.cta")} <ArrowRight size={14} />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="text-[11px] tracking-[0.4em] uppercase text-cyan-400 mb-4 flex items-center gap-2">
              <span className="inline-block w-6 h-px bg-cyan-400" />
              {project.mode?.toUpperCase()} · {project.genre} · {project.engine}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light leading-[1.05]" data-testid="showcase-title">
              {project.name}
            </h1>
            <p className="mt-6 text-sm sm:text-base text-zinc-400 max-w-2xl leading-relaxed">
              {project.description || project.gdd?.idea || "An indie game made with GameForge."}
            </p>

            <div className="mt-6 text-[11px] tracking-[0.3em] uppercase text-zinc-500 flex items-center gap-4 flex-wrap">
              <span>{t("showcase.by")} <span className="text-zinc-300">{project.owner_name || "Anonymous"}</span></span>
              <span className="text-zinc-700">·</span>
              <span className="flex items-center gap-1">
                <Heart size={12} weight="fill" className="text-pink-400" /> {project.remix_count} {t("showcase.remixes")}
              </span>
              {project.remix_of && (
                <>
                  <span className="text-zinc-700">·</span>
                  <Link to={`/showcase/${project.remix_of.slug}`} className="text-cyan-400 hover:underline">
                    {t("showcase.remixOf")} {project.remix_of.owner_name}
                  </Link>
                </>
              )}
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Button
                data-testid="showcase-remix-btn"
                onClick={handleRemix}
                disabled={remixing}
                className="bg-cyan-400 hover:bg-cyan-300 text-zinc-950 rounded-none font-semibold tracking-wide text-sm px-6 py-5 h-auto gap-2 group"
              >
                <ArrowsClockwise size={16} weight="bold" /> {remixing ? "..." : (user ? t("showcase.remix") : t("showcase.signin"))}
              </Button>
            </div>

            {/* Stats grid */}
            <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-px bg-zinc-800 border border-zinc-800">
              <Stat label="ENTITIES" value={totalEntities} tone="cyan" />
              <Stat label="SPRITES" value={(project.sprites || []).length} />
              <Stat label="ENEMIES" value={counts.enemies} />
              <Stat label="WEAPONS" value={counts.weapons} />
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative aspect-[4/3] border border-zinc-800 overflow-hidden bg-zinc-900">
              {project.thumbnail ? (
                <img src={project.thumbnail} alt={project.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-zinc-700">
                  <GameController size={64} weight="duotone" />
                  <span className="text-[10px] tracking-[0.3em] uppercase mt-3">// NO THUMBNAIL</span>
                </div>
              )}
              <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-[10px] tracking-[0.25em] uppercase text-zinc-300 bg-zinc-950/70 backdrop-blur px-3 py-2 border border-zinc-800">
                <span>// PREVIEW</span>
                <span className="text-cyan-400">{project.mode?.toUpperCase()}</span>
              </div>
            </div>

            {sprites.length > 0 && (
              <div className="mt-6">
                <div className="text-[10px] tracking-[0.4em] uppercase text-cyan-400 mb-3">// SPRITES</div>
                <div className="grid grid-cols-4 gap-px bg-zinc-800 border border-zinc-800">
                  {sprites.map((s) => (
                    <div key={s.id} className="bg-zinc-950 p-2 aspect-square flex items-center justify-center">
                      <img src={s.data_url} alt={s.name} className="max-w-full max-h-full" style={{ imageRendering: "pixelated" }} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Story / Idea / Goals */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-800 border border-zinc-800">
          <Block icon={Cpu} title={lang === "es" ? "Idea" : "Idea"} body={project.gdd?.idea} />
          <Block icon={Code} title={lang === "es" ? "Objetivo" : "Objective"} body={project.gdd?.objective} />
          <Block icon={Palette} title={lang === "es" ? "Historia" : "Story"} body={project.gdd?.story} />
        </div>

        {/* Goals */}
        {(project.gdd?.goals || []).length > 0 && (
          <div className="mt-12">
            <div className="text-[10px] tracking-[0.4em] uppercase text-cyan-400 mb-4">// GOALS</div>
            <ul className="space-y-2 text-sm text-zinc-300">
              {(project.gdd?.goals || []).map((g, i) => (
                <li key={i} className="flex gap-3 border-b border-zinc-800 pb-2">
                  <span className="text-cyan-400">{String(i + 1).padStart(2, "0")}</span>{g}
                </li>
              ))}
            </ul>
          </div>
        )}

        <footer className="mt-24 border-t border-zinc-800 pt-8 flex items-center justify-between text-[10px] tracking-[0.3em] uppercase text-zinc-500">
          <span>// GAMEFORGE SHOWCASE</span>
          <Link to="/dashboard" className="text-cyan-400 hover:underline">{t("showcase.cta")} →</Link>
        </footer>
      </main>
    </div>
  );
}

function Stat({ label, value, tone = "default" }) {
  const cls = tone === "cyan" ? "text-cyan-400" : "text-zinc-50";
  return (
    <div className="bg-zinc-950 px-4 py-4">
      <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500">{label}</div>
      <div className={"font-display text-2xl mt-1 " + cls}>{value}</div>
    </div>
  );
}

function Block({ icon: Icon, title, body }) {
  return (
    <div className="bg-zinc-950 p-6">
      <Icon size={20} weight="duotone" className="text-cyan-400 mb-3" />
      <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">{title}</div>
      <div className="text-sm text-zinc-300 whitespace-pre-wrap min-h-[60px]">{body || "—"}</div>
    </div>
  );
}